import { useRef, useState } from 'react';

const FONT_SANS = 'var(--font-brand)';

interface OtpInputProps {
  /** Number of digit boxes (default 6). */
  length?: number;
  /** Called with the joined code on every change. */
  onChange: (code: string) => void;
  /** Called once every box is filled (auto-submit hook). */
  onComplete?: (code: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Render a dash between the two halves (e.g. 724-8·· ), like most OTP UIs. */
  withSeparator?: boolean;
}

/**
 * Segmented one-time-code input — one box per digit, with auto-advance,
 * backspace-to-previous, and full-code paste. Numeric only. Controlled via the
 * joined string it reports through `onChange`; fires `onComplete` when full.
 *
 * Shared by the login MFA challenge and the email-verification flow so the
 * 6-box code entry is identical everywhere.
 */
export function OtpInput({
  length = 6,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
  withSeparator = false,
}: OtpInputProps) {
  const [digits, setDigits] = useState<Array<string>>(Array(length).fill(''));
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const commit = (next: Array<string>) => {
    setDigits(next);
    const code = next.join('');
    onChange(code);
    if (code.length === length && next.every((d) => d !== '')) {
      onComplete?.(code);
    }
  };

  const setDigit = (index: number, raw: string) => {
    const typed = raw.replace(/\D/g, '');
    // More than one digit in a single change is an AUTOFILL, not typing: iOS
    // and Android offer the texted code and write all six into the focused
    // box. `maxLength={1}` used to truncate that to its first digit and drop
    // the rest, so "Use code from Messages" filled one box.
    // Two is a box that already held a digit being typed over, not autofill.
    if (typed.length > 2) {
      const next = Array(length).fill('');
      for (let i = 0; i < length; i++) next[i] = typed[i] ?? '';
      commit(next);
      const firstEmpty = next.findIndex((d) => !d);
      inputsRef.current[firstEmpty === -1 ? length - 1 : firstEmpty]?.focus();
      return;
    }
    const value = typed.slice(-1);
    const next = [...digits];
    next[index] = value;
    commit(next);
    if (value && index < length - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, length);
    if (!pasted) return;
    const next = Array(length).fill('');
    for (let i = 0; i < length; i++) next[i] = pasted[i] ?? '';
    commit(next);
    const firstEmpty = next.findIndex((d) => !d);
    inputsRef.current[firstEmpty === -1 ? length - 1 : firstEmpty]?.focus();
  };

  return (
    <div
      className="flex gap-2 sm:gap-2.5 justify-center w-full"
      role="group"
      aria-label="One-time code"
    >
      {Array.from({ length }).map((_, index) => (
        <div key={index} className="flex items-center gap-2 sm:gap-2.5">
          <input
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            autoFocus={autoFocus && index === 0}
            value={digits[index]}
            onChange={(e) => setDigit(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            disabled={disabled}
            aria-label={`Digit ${index + 1}`}
            style={{ fontFamily: FONT_SANS }}
            // Tokens, not the Figma hexes, because this widget is rendered in TWO
            // places with opposite backgrounds: the auth screens, which are
            // pinned light by `AuthShell`, and Settings > Security, which follows
            // the theme. It used to be six `bg-white` boxes ~400px wide, so on a
            // dark Settings page it was a white slab inside a `bg-card` panel.
            //
            // Tokens rather than `dark:` variants, and that distinction matters:
            // the `dark` variant keys off `:is(.dark *)`, so it still matches
            // inside a `.light` island on a dark page and would fire on the auth
            // screens too. A token re-reads its value from the island. That is
            // the rule for anything rendered inside a pinned surface.
            className="border-input bg-input-background text-foreground focus:border-ring h-[72px] w-[58px] rounded-xl border-2 text-center text-[30px] font-semibold outline-none transition-colors disabled:opacity-60 sm:h-20 sm:w-[68px]"
          />
          {withSeparator && index === Math.floor(length / 2) - 1 && (
            <span className="text-muted-foreground text-[22px] select-none">
              –
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
